import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { useToastContext } from "@finitless/shared/contexts";

/**
 * Hook to handle integration sync operations
 * @param {Object} params - Parameters for sync operations
 * @param {string|number} params.businessId - Business ID
 * @param {string|number} params.integrationId - Integration ID
 * @returns {Object} Mutation functions for sync operations
 */
export const useIntegrationSync = ({ businessId, integrationId }) => {
  const toast = useToastContext();

  // Sync Business mutation
  const syncBusinessMutation = useMutation({
    mutationFn: async () => {
      if (!businessId || !integrationId) {
        throw new Error("Business ID and Integration ID are required");
      }

      const response = await apiClient.post(
        `/business/${businessId}/third_party_integrations/${integrationId}/sync_business`,
      );
      return response;
    },
    onSuccess: (data) => {
      const executedRequests = data?.result?.executed_requests || 0;
      toast.success(
        `Business data synchronized successfully. ${executedRequests} requests executed.`,
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          "Failed to sync business data. Please try again.",
      );
    },
    enabled: isEndpointEnabled("business", "list"), // Using business endpoint as reference
  });

  // Sync Menu mutation
  const syncMenuMutation = useMutation({
    mutationFn: async () => {
      if (!businessId || !integrationId) {
        throw new Error("Business ID and Integration ID are required");
      }

      const response = await apiClient.post(
        `/business/${businessId}/third_party_integrations/${integrationId}/sync_menu`,
      );
      return response;
    },
    onSuccess: (data) => {
      const executedRequests = data?.result?.executed_requests || 0;
      toast.success(
        `Menu data synchronized successfully. ${executedRequests} requests executed.`,
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          "Failed to sync menu data. Please try again.",
      );
    },
    enabled: isEndpointEnabled("business", "list"), // Using business endpoint as reference
  });

  return {
    syncBusiness: syncBusinessMutation.mutate,
    syncMenu: syncMenuMutation.mutate,
    isSyncingBusiness: syncBusinessMutation.isPending,
    isSyncingMenu: syncMenuMutation.isPending,
    syncBusinessError: syncBusinessMutation.error,
    syncMenuError: syncMenuMutation.error,
  };
};

export default useIntegrationSync;
